import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { NotificacionComponent } from '@shared/ui/notificacion/notificacion.component';
import { DataNotificacion } from '@shared/ui/notificacion/interfaces/data.notificacion';
import { TypeAlertEnum } from '@shared/enums/type.alert.enum';

/** API minima de Iconify (assets/js/iconify.min.js) usada para precarga. */
interface IconifyApi {
  loadIcons?: (icons: string[], callback?: () => void) => void;
}

/**
 * Servicio que permite mostrar una notificación personalizada
 *
 * @author Diego Armando Ortiz Bastidas <dortiz@heinsohn.com.co>
 */
@Injectable({
  providedIn: 'root'
})
export class NotificacionService {

  /** Ms tras open antes de revelar el toast (Material mueve el DOM a11y ~150ms). */
  private static readonly REVELAR_TOAST_MS = 180;

  // Inyecciones de dependencias
  private _snackBar = inject(MatSnackBar);

  /** Evita disparar loadIcons mas de una vez por sesion. */
  private static _iconosToastPrecargados = false;

  constructor() {
    this.precargarIconosToast();
  }

  /**
   * Muestra la notificacion con la configuracion recibida.
   *
   * @param dataAlertMensaje datos del toast
   * @param durationMs milisegundos para cierre automatico; omitir para dejarla hasta dismiss
   * @returns referencia al snackbar abierto
   */
  open(
    dataAlertMensaje: DataNotificacion,
    durationMs?: number | null
  ): MatSnackBarRef<NotificacionComponent> {
    // Por si el servicio se creo antes de que Iconify estuviera en window.
    this.precargarIconosToast();

    const ref = this._snackBar.openFromComponent(NotificacionComponent, {
      data: dataAlertMensaje,
      panelClass: ['custom-snackbar-container', 'm-t-120', 'm-r-32', 'h5-toast-pending'],
      verticalPosition: 'top',
      horizontalPosition: 'right',
      ...(durationMs != null && durationMs > 0 ? { duration: durationMs } : {})
    });

    // Revela tras el move a11y de Material (~150ms); ver _custom-snackbar.scss.
    setTimeout(() => {
      document.querySelectorAll('mat-snack-bar-container.h5-toast-pending').forEach((el) => {
        el.classList.remove('h5-toast-pending');
        el.classList.add('h5-toast-visible');
      });
    }, NotificacionService.REVELAR_TOAST_MS);

    return ref;
  }

  /**
   * Remueve la actual notificación
   *
   * @author Diego Armando Ortiz Bastidas <dortiz@heinsohn.com.co>
   */
  dismiss(): void {
    this._snackBar.dismiss();
  }

    /**
     * Muestra una notificación simple utilizando la configuración básica (tipo de alerta y mensaje).
     *
     * @param label Mensaje a mostrar en el tooltip
     * @param typeAlertEnum Tipo de alerta
     * @param duration Duración en milisegundo para ser cerrado automáticamente (Opcional)
     *
     * @author parias
     */
    public mostrarNotificacionBasica(label: string, typeAlertEnum: TypeAlertEnum, duration:number | null = null): void {
        this.open({ typeAlertEnum, label }, duration);
    }

  /**
   * Precarga en cache de Iconify los iconos usados por TypeAlertEnum.
   * Evita el pop del SVG en el primer toast (paginas auth en frio).
   */
  private precargarIconosToast(): void {
    if (NotificacionService._iconosToastPrecargados) {
      return;
    }
    const iconify = this.obtenerIconify();
    if (!iconify?.loadIcons) {
      return;
    }
    NotificacionService._iconosToastPrecargados = true;
    iconify.loadIcons(Object.values(TypeAlertEnum));
  }

  private obtenerIconify(): IconifyApi | undefined {
    return (window as Window & { Iconify?: IconifyApi }).Iconify;
  }

}
